import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit3, Check, X, Upload, Building2, HardHat, ClipboardCheck, ShieldCheck, Wrench, Users } from 'lucide-react';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const PARTNER_TYPES = [
  { value: 'SYNDIC', label: 'Syndic / Maître d\'ouvrage', icon: Building2 },
  { value: 'MAITRE_OEUVRE', label: 'Maître d\'œuvre', icon: HardHat },
  { value: 'BUREAU_CONTROLE', label: 'Bureau de contrôle', icon: ClipboardCheck },
  { value: 'COORDINATEUR_SPS', label: 'Coordinateur SPS', icon: ShieldCheck },
  { value: 'ENTREPRISE', label: 'Entreprise', icon: Wrench },
  { value: 'OTHER', label: 'Autre', icon: Users },
];

const typeLabel = (type) => PARTNER_TYPES.find(t => t.value === type)?.label || type;

const emptyForm = {
  type: 'ENTREPRISE',
  name: '',
  contactName: '',
  contactTitle: '',
  address: '',
  phone: '',
  email: '',
};

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [filterType, setFilterType] = useState('ALL');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/partners');
      setPartners(data);
    } catch {
      toast.error('Erreur chargement des intervenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setLogoFile(null);
    setLogoPreview(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      type: p.type,
      name: p.name,
      contactName: p.contactName || '',
      contactTitle: p.contactTitle || '',
      address: p.address || '',
      phone: p.phone || '',
      email: p.email || '',
    });
    setLogoFile(null);
    setLogoPreview(p.logo ? `${API_BASE}/uploads/${p.logo}` : null);
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    try {
      let partnerId = editingId;
      if (editingId) {
        await api.put(`/partners/${editingId}`, form);
      } else {
        const { data } = await api.post('/partners', form);
        partnerId = data.id;
      }
      if (logoFile && partnerId) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        await api.post(`/partners/${partnerId}/logo`, fd);
      }
      toast.success(editingId ? 'Intervenant mis à jour' : 'Intervenant ajouté');
      cancel();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet intervenant ?')) return;
    try {
      await api.delete(`/partners/${id}`);
      toast.success('Supprimé');
      load();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const uploadLogo = async (partnerId, file) => {
    if (!file) return;
    setUploadingLogo(partnerId);
    const fd = new FormData();
    fd.append('logo', file);
    try {
      await api.post(`/partners/${partnerId}/logo`, fd);
      load();
      toast.success('Logo mis à jour');
    } catch {
      toast.error('Erreur upload logo');
    } finally {
      setUploadingLogo(null);
    }
  };

  const filtered = filterType === 'ALL' ? partners : partners.filter(p => p.type === filterType);

  const grouped = PARTNER_TYPES.reduce((acc, t) => {
    const items = filtered.filter(p => p.type === t.value);
    if (items.length > 0) acc.push({ ...t, items });
    return acc;
  }, []);

  if (loading) return <div className="container" style={{ padding: 40, textAlign: 'center' }}>Chargement...</div>;

  return (
    <div className="container" style={{ padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>Intervenants</h2>
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginTop: 4 }}>
            Gérez votre carnet d'adresses : syndics, maîtres d'œuvre, bureaux de contrôle, entreprises…
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} /> Ajouter
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className={`btn btn-sm ${filterType === 'ALL' ? 'btn-primary' : ''}`} onClick={() => setFilterType('ALL')}>
          Tous ({partners.length})
        </button>
        {PARTNER_TYPES.map(t => {
          const count = partners.filter(p => p.type === t.value).length;
          if (count === 0) return null;
          return (
            <button key={t.value} className={`btn btn-sm ${filterType === t.value ? 'btn-primary' : ''}`}
              onClick={() => setFilterType(t.value)}>
              {t.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20, border: '2px solid var(--primary)' }}>
          <h3 style={{ marginBottom: 16 }}>{editingId ? 'Modifier l\'intervenant' : 'Nouvel intervenant'}</h3>

          {/* Logo upload */}
          <div className="form-group">
            <label>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 80, height: 64, borderRadius: 8, border: '2px dashed var(--border)', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                  : <span style={{ fontSize: '1.6rem', color: '#ccc' }}>🏢</span>
                }
              </div>
              <label className="btn" style={{ cursor: 'pointer' }}>
                <Upload size={14} /> {logoPreview ? 'Changer le logo' : 'Charger un logo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
              </label>
              {logoPreview && logoFile && (
                <button className="btn btn-sm" onClick={() => { setLogoFile(null); setLogoPreview(editingId ? null : null); }}>
                  <X size={14} /> Retirer
                </button>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type *</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                {PARTNER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Nom de l'entreprise / organisme *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: IMMO DE France, Groupe Qualiconsult…" autoFocus />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Nom du contact</label>
              <input value={form.contactName} onChange={e => setForm({ ...form, contactName: e.target.value })}
                placeholder="Ex: Jean Laurent Rieth" />
            </div>
            <div className="form-group">
              <label>Titre / fonction</label>
              <input value={form.contactTitle} onChange={e => setForm({ ...form, contactTitle: e.target.value })}
                placeholder="Ex: Architecte D.P.L.G" />
            </div>
          </div>
          <div className="form-group">
            <label>Adresse</label>
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
              placeholder="Ex: 131 rue Michel Ange, 75016 PARIS" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Téléphone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="Ex: 01 46 99 01 01" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="contact@exemple.fr" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={submit}><Check size={16} /> Enregistrer</button>
            <button className="btn" onClick={cancel}><X size={16} /> Annuler</button>
          </div>
        </div>
      )}

      {/* Partners list */}
      {partners.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
          <Users size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>Aucun intervenant enregistré.</p>
          <p style={{ fontSize: '0.9rem' }}>Ajoutez vos partenaires habituels pour les retrouver facilement sur vos chantiers.</p>
          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={openCreate}>
            <Plus size={16} /> Ajouter le premier
          </button>
        </div>
      ) : (
        grouped.map(group => (
          <div key={group.value} style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: '0.95rem', color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              {group.label}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {group.items.map(p => (
                <div key={p.id} className="card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* Logo */}
                  <div style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden', background: '#f8f9fa', border: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {p.logo ? (
                      <img src={`${API_BASE}/uploads/${p.logo}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                    ) : (
                      <span style={{ fontSize: '1.4rem', color: '#ccc' }}>🏢</span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{p.name}</div>
                    {p.contactName && (
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                        {p.contactName}{p.contactTitle ? ` — ${p.contactTitle}` : ''}
                      </div>
                    )}
                    {p.address && <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{p.address}</div>}
                    {p.phone && <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Tél : {p.phone}</div>}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <label className="btn btn-sm" style={{ cursor: 'pointer' }} title="Changer le logo">
                      <Upload size={14} /> {uploadingLogo === p.id ? '...' : 'Logo'}
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => uploadLogo(p.id, e.target.files[0])} disabled={uploadingLogo === p.id} />
                    </label>
                    <button className="btn btn-sm" onClick={() => openEdit(p)} title="Modifier">
                      <Edit3 size={14} />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(p.id)} title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
